import {
  collectionsApi,
  slotsApi,
  topicsApi,
  type Collection,
  type TopicDetail,
} from "@/lib/api/admin";
import { AlertTriangle, ArrowLeft, Bot, ClipboardList, Pencil, Star, Trash2, X } from "lucide-react";
import { useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import AIGenerate from "../../components/AIGenerate";
import SlotBulkEdit from "../../components/SlotBulkEdit";
import SlotImport from "../../components/SlotImport";
import { Toasts, useToasts } from "@/app/admin/components/Toasts";
import { useConfirm } from "@/app/admin/components/ConfirmDialog";
import styles from "./page.module.css";

const CATEGORIES = [
  "Geography",
  "Sports",
  "Film & TV",
  "Music",
  "Food & Drink",
  "Science & Nature",
  "History & Politics",
  "Tech & Gaming",
  "Lifestyle",
  "Pop Culture",
] as const;

export default function TopicDetailPage({ id }: { id: string }) {
  const navigate = useNavigate();
  const { toasts, showToast } = useToasts();
  const { confirm, confirmDialog } = useConfirm();
  const topicId = parseInt(id);

  const [topic, setTopic] = useState<TopicDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingMetadata, setEditingMetadata] = useState(false);
  const [topicName, setTopicName] = useState("");
  const [topicPrompt, setTopicPrompt] = useState("");
  const [topicExample, setTopicExample] = useState("");
  const [topicCategory, setTopicCategory] = useState("");
  const [collections, setCollections] = useState<Collection[]>([]);
  const [showCreateSlot, setShowCreateSlot] = useState(false);
  const [showAIGenerate, setShowAIGenerate] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [showBulkEdit, setShowBulkEdit] = useState(false);

  // New slot form state
  const [newSlotCanonical, setNewSlotCanonical] = useState("");
  const [newSlotPrompt, setNewSlotPrompt] = useState("");
  const [newSlotBobClue, setNewSlotBobClue] = useState("");
  const [newSlotIsRare, setNewSlotIsRare] = useState(false);

  useEffect(() => {
    loadData();
  }, [topicId]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Load topic details with slots and all collections for display
      const [topicData, collectionsData] = await Promise.all([
        topicsApi.getById(topicId),
        collectionsApi.getAll(),
      ]);
      setTopic(topicData);
      setTopicName(topicData.name);
      setTopicPrompt(topicData.prompt || "");
      setTopicExample(topicData.example_text || "");
      setTopicCategory(topicData.category || "");
      setCollections(collectionsData);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load topic");
    } finally {
      setLoading(false);
    }
  };

  const handleSaveMetadata = async () => {
    try {
      await topicsApi.update(topicId, {
        name: topicName,
        prompt: topicPrompt || undefined,
        example_text: topicExample || undefined,
        category: topicCategory || undefined,
      });
      setEditingMetadata(false);
      showToast("Topic updated");
      loadData();
    } catch (err) {
      showToast(err instanceof Error ? err.message : "Failed to update topic", "error");
    }
  };

  const handleCreateSlot = async () => {
    if (!newSlotCanonical.trim() || !newSlotPrompt.trim()) {
      showToast("Canonical text and prompt are required", "error");
      return;
    }

    try {
      await slotsApi.create(topicId, {
        canonical_text: newSlotCanonical,
        prompt: newSlotPrompt,
        bot_bob_clue: newSlotBobClue || undefined,
        is_rare: newSlotIsRare,
        topic_id: topicId,
      });

      // Reset form
      setNewSlotCanonical("");
      setNewSlotPrompt("");
      setNewSlotBobClue("");
      setNewSlotIsRare(false);
      setShowCreateSlot(false);
      showToast("Slot created");
      loadData();
    } catch (err) {
      showToast(err instanceof Error ? err.message : "Failed to create slot", "error");
    }
  };

  const handleDeleteSlot = async (slotId: number) => {
    if (
      !(await confirm({
        title: "Delete this slot?",
        message: "This will also delete all its aliases.",
        confirmLabel: "Delete",
        danger: true,
      }))
    ) {
      return;
    }

    try {
      await slotsApi.delete(slotId);
      showToast("Slot deleted");
      loadData();
    } catch (err) {
      showToast(err instanceof Error ? err.message : "Failed to delete slot", "error");
    }
  };

  const handleViewSlot = (slotId: number) => {
    navigate({ to: `/admin/slots/${slotId}` });
  };

  const getCollectionNames = (collectionIds: number[]) => {
    if (!collectionIds || collectionIds.length === 0) {
      return "Not assigned to any collection";
    }
    return collectionIds
      .map(
        (id) =>
          collections.find((collection) => collection.id === id)?.name ||
          `Collection ${id}`,
      )
      .join(", ");
  };

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.loading}>
          <div className={styles.spinner}></div>
          <p>LOADING TOPIC...</p>
        </div>
      </div>
    );
  }

  if (error || !topic) {
    return (
      <div className={styles.container}>
        <div className={styles.error}>
          <p><AlertTriangle size={16} /> {error || "Topic not found"}</p>
          <button onClick={() => navigate({ to: "/admin/collections" })}>
            <ArrowLeft size={16} /> BACK TO COLLECTIONS
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <main className={styles.main}>
        <div className={styles.header}>
          <h1 className={styles.title}>
            <span className={styles.neonText}>EDIT</span>
            <span className={styles.neonTextPink}>TOPIC</span>
          </h1>

          <button className={styles.backButton} onClick={() => window.history.back()}>
            <ArrowLeft size={16} /> BACK
          </button>
        </div>

        <div className={styles.content}>
          {/* Topic Metadata */}
          <div className={styles.metadataSection}>
            {editingMetadata ? (
              <div className={styles.editForm}>
                <div className={styles.formField}>
                  <label className={styles.formLabel}>Topic Name</label>
                  <input
                    type="text"
                    className={styles.input}
                    value={topicName}
                    onChange={(e) => setTopicName(e.target.value)}
                    placeholder="Topic name"
                  />
                </div>
                <div className={styles.formField}>
                  <label className={styles.formLabel}>Prompt</label>
                  <textarea
                    className={styles.textarea}
                    value={topicPrompt}
                    onChange={(e) => setTopicPrompt(e.target.value)}
                    placeholder="Optional prompt shown to players"
                    rows={2}
                  />
                </div>
                <div className={styles.formField}>
                  <label className={styles.formLabel}>Example Text</label>
                  <input
                    type="text"
                    className={styles.input}
                    value={topicExample}
                    onChange={(e) => setTopicExample(e.target.value)}
                    placeholder="Optional example"
                  />
                </div>
                    <div className={styles.formField}>
                  <label className={styles.formLabel}>Category</label>
                  <select
                    className={styles.select}
                    value={topicCategory}
                    onChange={(e) => setTopicCategory(e.target.value)}
                  >
                    <option value="">— No category —</option>
                    {CATEGORIES.map((cat) => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                </div>
                <div className={styles.editActions}>
                  <button
                    className={styles.cancelButton}
                    onClick={() => {
                      setEditingMetadata(false);
                      setTopicName(topic.name);
                      setTopicPrompt(topic.prompt || "");
                      setTopicExample(topic.example_text || "");
                      setTopicCategory(topic.category || "");
                    }}
                  >
                    CANCEL
                  </button>
                  <button
                    className={styles.saveButton}
                    onClick={handleSaveMetadata}
                  >
                    SAVE
                  </button>
                </div>
              </div>
            ) : (
              <div className={styles.metadataDisplay}>
                <div>
                  <h2 className={styles.topicName}>{topic.name}</h2>
                  {topic.prompt && (
                    <p className={styles.topicPrompt}>
                      <strong>Prompt:</strong> {topic.prompt}
                    </p>
                  )}
                  {topic.example_text && (
                    <p className={styles.topicExample}>
                      <strong>Example:</strong> {topic.example_text}
                    </p>
                  )}
                  {topic.category && (
                    <p className={styles.topicCategory}>
                      <strong>Category:</strong> <span className={styles.categoryBadge}>{topic.category}</span>
                    </p>
                  )}
                  <p className={styles.topicCollections}>
                    <strong>Collections:</strong>{" "}
                    {getCollectionNames(topic.collection_ids || [])}
                  </p>
                </div>
                <button
                  className={styles.editMetadataButton}
                  onClick={() => setEditingMetadata(true)}
                >
                  <Pencil size={16} /> EDIT
                </button>
              </div>
            )}
          </div>

          {/* Slots Section */}
          <div className={styles.slotsSection}>
            <div className={styles.slotsSectionHeader}>
              <h2 className={styles.sectionTitle}>
                Slots ({topic.slots.length})
              </h2>
              <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
                <button
                  className={styles.createSlotButton}
                  style={{
                    background: "rgba(255,0,255,0.2)",
                    borderColor: "#ff00ff",
                    color: "#ff00ff",
                  }}
                  onClick={() => {
                    setShowAIGenerate(true);
                    setShowCreateSlot(false);
                    setShowImport(false);
                    setShowBulkEdit(false);
                  }}
                >
                  <Bot size={16} /> MORE SLOTS
                </button>
                <button
                  className={styles.createSlotButton}
                  style={{
                    background: showBulkEdit
                      ? "rgba(0,255,100,0.15)"
                      : undefined,
                    borderColor: showBulkEdit ? "#00ff64" : undefined,
                    color: showBulkEdit ? "#00ff64" : undefined,
                  }}
                  onClick={() => {
                    setShowBulkEdit(!showBulkEdit);
                    setShowImport(false);
                    setShowCreateSlot(false);
                    setShowAIGenerate(false);
                  }}
                >
                  {showBulkEdit ? <><X size={16} /> CANCEL</> : <><Pencil size={16} /> BULK EDIT</>}
                </button>
                <button
                  className={styles.createSlotButton}
                  style={{
                    background: showImport ? "rgba(0,221,255,0.2)" : undefined,
                    borderColor: showImport ? "#00ddff" : undefined,
                    color: showImport ? "#00ddff" : undefined,
                  }}
                  onClick={() => {
                    setShowImport(!showImport);
                    setShowCreateSlot(false);
                    setShowAIGenerate(false);
                    setShowBulkEdit(false);
                  }}
                >
                  {showImport ? <><X size={16} /> CANCEL</> : <><ClipboardList size={16} /> IMPORT JSON</>}
                </button>
                <button
                  className={styles.createSlotButton}
                  onClick={() => {
                    setShowCreateSlot(!showCreateSlot);
                    setShowAIGenerate(false);
                    setShowImport(false);
                    setShowBulkEdit(false);
                  }}
                >
                  {showCreateSlot ? <><X size={16} /> CANCEL</> : "+ NEW SLOT"}
                </button>
              </div>
            </div>

            {/* AI Generate More Slots */}
            {showAIGenerate && (
              <AIGenerate
                topicName={topic.name}
                onComplete={() => {
                  loadData();
                  setShowAIGenerate(false);
                }}
                onClose={() => setShowAIGenerate(false)}
                title={<><Bot size={16} /> Generate More Slots</>}
              />
            )}

            {/* Bulk Edit */}
            {showBulkEdit && (
              <SlotBulkEdit
                existingSlots={topic.slots}
                onComplete={() => {
                  loadData();
                  setShowBulkEdit(false);
                }}
                onClose={() => setShowBulkEdit(false)}
              />
            )}

            {/* JSON Import */}
            {showImport && (
              <SlotImport
                topicId={topic.id}
                existingSlots={topic.slots}
                onComplete={() => {
                  loadData();
                  setShowImport(false);
                }}
                onClose={() => setShowImport(false)}
              />
            )}

            {/* Create Slot Form */}
            {showCreateSlot && (
              <div className={styles.createSlotForm}>
                <div className={styles.formRow}>
                  <div className={styles.formField}>
                    <label className={styles.formLabel}>Canonical Text *</label>
                    <input
                      type="text"
                      className={styles.input}
                      value={newSlotCanonical}
                      onChange={(e) => setNewSlotCanonical(e.target.value)}
                      placeholder="The main answer text"
                    />
                  </div>
                  <div className={styles.formField}>
                    <label className={styles.formLabel}>Prompt *</label>
                    <input
                      type="text"
                      className={styles.input}
                      value={newSlotPrompt}
                      onChange={(e) => setNewSlotPrompt(e.target.value)}
                      placeholder="Clue or hint for players"
                    />
                  </div>
                </div>
                <div className={styles.formRow}>
                  <div className={styles.formField}>
                    <label className={styles.formLabel}>Bot Bob Clue</label>
                    <input
                      type="text"
                      className={styles.input}
                      value={newSlotBobClue}
                      onChange={(e) => setNewSlotBobClue(e.target.value)}
                      placeholder="Optional bot clue"
                    />
                  </div>
                  <div className={styles.formField}>
                    <label className={styles.checkboxLabel}>
                      <input
                        type="checkbox"
                        checked={newSlotIsRare}
                        onChange={(e) => setNewSlotIsRare(e.target.checked)}
                        className={styles.checkbox}
                      />
                      <span>Is Rare</span>
                    </label>
                  </div>
                </div>
                <div className={styles.formActions}>
                  <button
                    className={styles.cancelButton}
                    onClick={() => {
                      setShowCreateSlot(false);
                      setNewSlotCanonical("");
                      setNewSlotPrompt("");
                      setNewSlotBobClue("");
                      setNewSlotIsRare(false);
                    }}
                  >
                    CANCEL
                  </button>
                  <button
                    className={styles.saveButton}
                    onClick={handleCreateSlot}
                  >
                    CREATE SLOT
                  </button>
                </div>
              </div>
            )}

            {/* Slots List */}
            <div className={styles.slotsList}>
              {topic.slots.length === 0 ? (
                <p className={styles.emptyMessage}>
                  No slots yet. Create your first slot above.
                </p>
              ) : (
                topic.slots.map((slot) => (
                  <div key={slot.id} className={styles.slotCard}>
                    <div className={styles.slotInfo}>
                      <div className={styles.slotHeader}>
                        <span className={styles.slotCanonical}>
                          {slot.canonical_text}
                        </span>
                        {slot.is_rare && (
                          <span className={styles.rareBadge}><Star size={14} /> RARE</span>
                        )}
                      </div>
                      <p className={styles.slotPrompt}>
                        <strong>Prompt:</strong> {slot.prompt}
                      </p>
                      {slot.bot_bob_clue && (
                        <p className={styles.slotBobClue}>
                          <strong>Bot Clue:</strong> {slot.bot_bob_clue}
                        </p>
                      )}
                      <p className={styles.slotAliasCount}>
                        {slot.alias_count || 0} alias(es)
                      </p>
                    </div>
                    <div className={styles.slotActions}>
                      <button
                        className={styles.viewButton}
                        onClick={() => handleViewSlot(slot.id)}
                      >
                        <Pencil size={16} /> EDIT
                      </button>
                      <button
                        className={styles.deleteButton}
                        onClick={() => handleDeleteSlot(slot.id)}
                      >
                        <Trash2 size={16} /> DELETE
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </main>

      <Toasts toasts={toasts} />
      {confirmDialog}
    </div>
  );
}
